/**
 * PATH: packages/ui/src/kanban/api-fetch/card-attachments.ts
 * FILE_ID: UI-KANBAN-FETCH-CARD-ATTACHMENTS-001
 * PURPOSE: Card attachment read, upload, and delete transport methods.
 * WHY: Keeps the primary card API factory within the 500-line ceiling.
 * FLOW: KanbanApi attachment method -> shared/authenticated HTTP request.
 */
import type { CardAttachment } from '../types'
import type { KanbanApi } from '../api'
import { formatDrfError } from '../formatDrfError'
import type { ApiFetchContext } from './context'

export function createCardAttachmentsApi(ctx: ApiFetchContext) {
  const { _request, getAuthHeaders, baseUrl } = ctx
  return {
    // Card file attachments (FEAT-2026-06-29-KANBAN-CARD-FILE-UPLOAD) -
    // List + delete go through the JSON `_request` helper. Upload is a
    // multipart POST (mirrors `transcribeAudio` above) — do NOT set
    // Content-Type; the browser adds the multipart boundary for FormData.
    // Files become real QDrive FileRecords server-side (source_type=
    // 'kanban_card'); the response is the same `serialize_file_summary`
    // shape QDrive uses (`CardAttachment`).
    listCardAttachments: (cardId) =>
      _request<CardAttachment[]>('GET', `/cards/${cardId}/attachments/`),
    uploadCardAttachment: async (cardId, file) => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (getAuthHeaders) {
        const extra = await getAuthHeaders()
        Object.assign(headers, extra)
      }
      const fd = new FormData()
      fd.append('file', file, file.name)
      const cleanBase = baseUrl.replace(/\/+$/, '')
      const url = `${cleanBase}/cards/${cardId}/attachments/`
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        let err = `upload failed: ${res.status}`
        try {
          const j = await res.json()
          const parsed = formatDrfError(j)
          if (parsed) err = parsed
        } catch {
          // ignore body parse error
        }
        const error = new Error(err) as Error & { status?: number }
        error.status = res.status
        throw error
      }
      return (await res.json()) as CardAttachment
    },
    deleteCardAttachment: async (cardId, fileId) => {
      await _request<void>('DELETE', `/cards/${cardId}/attachments/${fileId}/`)
    },
  } satisfies Partial<KanbanApi>
}
